import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles, Plus, X, FlaskConical, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const CRIMSON = "var(--brand)";

export function MasterAiConfig() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newModel, setNewModel] = useState({ id: "", label: "" });
  const [testStates, setTestStates] = useState({});

  useEffect(() => {
    api.masterAiConfig()
      .then(setCfg)
      .catch(() => toast.error("Could not load Master AI settings"));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      setCfg(await api.saveMasterAiConfig(cfg));
      toast.success("Master AI settings saved — sub-systems will use the updated model list");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const addModel = () => {
    const id = newModel.id.trim();
    if (!id) return toast.error("Enter the Bedrock model ID");
    if ((cfg.models || []).some((m) => m.id === id)) return toast.error("That model is already in the registry");
    const label = newModel.label.trim() || id;
    setCfg((c) => ({ ...c, models: [...(c.models || []), { id, label }] }));
    setNewModel({ id: "", label: "" });
    toast.success("Model added to registry — click Save to persist");
  };

  const removeModel = (id) => setCfg((c) => ({ ...c, models: (c.models || []).filter((m) => m.id !== id) }));

  const updateModelLabel = (id, label) =>
    setCfg((c) => ({ ...c, models: (c.models || []).map((m) => m.id === id ? { ...m, label } : m) }));

  const testModel = async (modelId) => {
    setTestStates((s) => ({ ...s, [modelId]: "testing" }));
    try {
      await api.testMasterAiModel({ model_id: modelId });
      setTestStates((s) => ({ ...s, [modelId]: "ok" }));
    } catch (e) {
      setTestStates((s) => ({ ...s, [modelId]: "fail" }));
      toast.error(e.response?.data?.detail || `Test failed for ${modelId}`);
    }
  };

  if (!cfg) return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 flex justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4" style={{ color: CRIMSON }} />
          <h2 className="text-sm font-semibold text-slate-800">Master AI Settings</h2>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          Single source of truth for all AI integrations. Add or remove Bedrock models here and they
          become available across all sub-systems (Approvals AI, Issue Tracker AI, Document Builder, Documents AI, Audit Prep AI, Incident summaries).
          Each sub-system selects which model to use and configures its own behaviour independently.
        </p>
        <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-1">
          <div><span className="font-medium text-slate-700">Provider:</span> AWS Bedrock (ap-southeast-2)</div>
          <div>
            <span className="font-medium text-slate-700">Credentials:</span>{" "}
            <code className="bg-slate-100 px-1 rounded">AWS_ACCESS_KEY_ID</code> /{" "}
            <code className="bg-slate-100 px-1 rounded">AWS_SECRET_ACCESS_KEY</code> environment variables
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-1">Model Registry</h3>
        <p className="text-xs text-slate-500 mb-4">
          All Bedrock models available across the platform. Add any on-demand model ID or
          cross-region inference profile. Use &ldquo;Test&rdquo; to check connectivity without saving.
        </p>

        <div className="space-y-2 mb-4">
          {(cfg.models || []).map((m) => {
            const ts = testStates[m.id] || "idle";
            return (
              <div key={m.id} className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex-1 min-w-0">
                  <Input
                    value={m.label}
                    onChange={(e) => updateModelLabel(m.id, e.target.value)}
                    className="text-xs h-7 font-medium mb-1"
                    placeholder="Display label"
                  />
                  <div className="text-[10px] text-slate-400 font-mono truncate pl-1">{m.id}</div>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs shrink-0"
                  onClick={() => testModel(m.id)} disabled={ts === "testing"} title="Test connectivity">
                  {ts === "testing" && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                  {ts === "ok" && <CheckCircle2 className="h-3 w-3 text-emerald-500 mr-1" />}
                  {ts === "fail" && <AlertCircle className="h-3 w-3 text-red-500 mr-1" />}
                  {ts === "idle" && <FlaskConical className="h-3 w-3 mr-1" />}
                  {ts === "testing" ? "Testing…" : ts === "ok" ? "OK" : ts === "fail" ? "Failed" : "Test"}
                </Button>
                <button onClick={() => removeModel(m.id)}
                  className="text-slate-400 hover:text-red-500 transition-colors shrink-0"
                  aria-label={`Remove ${m.label}`}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          })}
          {(cfg.models || []).length === 0 && (
            <div className="text-xs text-slate-400 italic p-3 text-center border border-dashed border-slate-200 rounded-lg">
              No models registered. Add one below.
            </div>
          )}
        </div>

        <div className="border border-dashed border-slate-300 rounded-lg p-3 bg-slate-50/60">
          <div className="text-xs font-medium text-slate-600 mb-2">Add a model</div>
          <div className="flex gap-2">
            <Input
              placeholder="Model ID (e.g. au.anthropic.claude-haiku-4-5-20251001-v1:0)"
              value={newModel.id}
              onChange={(e) => setNewModel((m) => ({ ...m, id: e.target.value }))}
              className="text-xs flex-1"
              onKeyDown={(e) => e.key === "Enter" && addModel()}
            />
            <Input
              placeholder="Display label (optional)"
              value={newModel.label}
              onChange={(e) => setNewModel((m) => ({ ...m, label: e.target.value }))}
              className="text-xs w-48"
            />
            <Button onClick={addModel} size="sm" variant="outline" className="text-xs shrink-0">
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs font-medium text-slate-600 mb-1 block">Notes (internal reference only)</label>
          <Textarea
            rows={2}
            value={cfg.notes || ""}
            onChange={(e) => setCfg((c) => ({ ...c, notes: e.target.value }))}
            className="text-xs"
            placeholder="Any notes about the AWS account, model access, billing limits…"
          />
        </div>

        <div className="mt-4">
          <Button onClick={save} disabled={busy} style={{ backgroundColor: CRIMSON }} className="text-white">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save Master AI Settings
          </Button>
        </div>
      </div>
    </div>
  );
}

